import { CSSProperties, ReactNode } from 'react';

import { isEmpty } from 'lodash/fp';

import { Alert, FlexColumn, Loading, Pagination, Table, TableBody, TableHead } from '../';
import _ from '../../locale';
import renderErrors from '../../utility/renderErrors';
import TableEmptyText from '../TableEmptyText';

export interface Props {
    page: number;
    perPage: number;
    onPageChange: (currentPage: number) => void;
    renderHeaders: () => ReactNode;
    renderItem: (item: {}) => ReactNode;
    renderEmpty?: () => ReactNode;
    areItemsLoaded: boolean;
    items: Array<{}> | null;
    totalItems: number;
    errors: {} | null;
    hidePagination?: boolean;
    dataTableStyle?: CSSProperties;
}

const DataTableView = ({
    errors,
    items,
    renderHeaders,
    renderItem,
    renderEmpty,
    totalItems,
    perPage,
    page,
    onPageChange,
    areItemsLoaded,
    hidePagination,
    dataTableStyle,
}: Props) => {
    const emptyText = renderEmpty?.() ?? _('lists_list_is_empty');

    return (
        <div style={{ height: '100%' }} data-testid="data-table-container">
            {errors && (
                <FlexColumn>
                    <Alert color="error" data-testid="error-message">
                        {renderErrors(errors)}
                    </Alert>
                </FlexColumn>
            )}

            {!isEmpty(items) && (
                <>
                    <Table stripes style={{ ...dataTableStyle }} data-testid="data-table">
                        <TableHead>{renderHeaders()}</TableHead>
                        <TableBody>{items.map(renderItem)}</TableBody>
                    </Table>

                    {!hidePagination && totalItems > perPage && (
                        <Pagination
                            data-testid="pagination"
                            totalItems={totalItems}
                            currentPage={page}
                            onChangePage={onPageChange}
                            rowsPerPage={perPage}
                        />
                    )}
                </>
            )}

            {!areItemsLoaded && !errors && (
                <FlexColumn>
                    <Loading data-testid="loading" />
                </FlexColumn>
            )}

            {areItemsLoaded && items && items.length === 0 && (
                <TableEmptyText data-testid="no-data-message">{emptyText}</TableEmptyText>
            )}
        </div>
    );
};

export default DataTableView;
